import { observable } from 'mobx';

export interface AccountInfo {
  id: string;
  email: string | null;
  name: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lastLoginAt: string | null;
  company: string | null;
}
export interface UserInfo extends AccountInfo {
  app: string | null;
  token: string | undefined;
  permissions: string[];
  loginPermissions?: string[];
  extraInfo?: any;
}
export enum DetailSettings {
  'autolock' = 'autolock',
  'occupied' = 'occupied',
  'default' = 'default',
}

export interface CustomerInfo {
  id: string;
  name: string | null;
  cities: string | null;
  logo: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SendMessageItem {
  alert: {
    title: string | null;
    body: string | null;
  };
  sound: string;
  custom_data: any;
}

export interface BoardcastMessageItem {
  id: string;
  alert: {
    title: string | null;
    body: string | null;
  };
  sound: string;
  custom_data: {
    type?: string;
    link?: string;
    important?: boolean;
  };
}

export interface BoardcastMessageHistoryItem {
  createdAt: string;
  finished: boolean;
  finishedAt: string;
  id: string;
  jobPickedAt: string;
  notificationData: BoardcastMessageItem;
  ready: boolean;
  userId: string;
}

export interface BoardcastMessageHistoryApiResponse {
  items: BoardcastMessageHistoryItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface QRLinkData {
  id: string;
  srcFamilyId: string;
  srcFamilyName: string;
  ownerId: string;
  destFamilyId: string;
  destUserId: string;
  createdAt: string;
}

export interface FamilyInfo {
  id: string;
  name: string;
  buildingId: string | null;
  userId: string;
  app: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  detailSetting: DetailSettings;
  adminNo?: string;
  order: number;
  qrLink?: QRLinkData;
  qrLinkLocked?: boolean;
}
export interface SubAccountInfo {
  id: string;
  name: string;
  email: string | null;
  app: string | null;
  parentAccountId: string;
  permissions: [];
  lastLoginAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface GranteeInfo {
  id: string;
  granteeEmail: string;
  granteeId: string;
  granteeToken: string;
  grantorId: string;
  granteeUserId: string;
  granteeName?: string;
  createdAt: string;
  lastImpersonateAt?: string;
  updatedAt: string;
}

export interface ImpersonateLoginResponse {
  grantorId: string;
  impersonating: boolean;
  token: string;
  user: UserInfo & {
    parentAccountId: string | null;
    company?: string | null;
  };
}

export interface BuildingInfo {
  id: string;
  name: string;
  userId: string;
  groupId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface GroupInfo {
  id: string;
  name: string;
  userId: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface RoomInfo {
  id: string;
  name: string;
  familyId: string;
  userId: string;
  linked: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DeviceInfo {
  id: string;
  did: string;
  pid: string;
  familyId: string;
  parentId: string | null;
  roomId: string | null;
  userId: string;

  name: string;
  appearance: string;
  linked: boolean;
  vendor: string;
  hidden: string | null;
  pinnedAt: string | null;
  extraInfo: any;
  setting: any;

  createdAt: string | null;
  updatedAt: string | null;
  isOnline?: boolean;

  // added by fe only
  renderExtraComponent?: boolean;
}

export interface PanelInfo {
  brandId: string;
  codeSpecId: string;
  customCodes: object[] | null;
  id: string;
  type: string;
  deviceId: string;
  familyId: string;
  roomId: string | null;
  userId: string;

  name: string;
  appearance: string;
  linked: boolean;
  hidden: string | null;
  pinnedAt: string | null;
  state: object | null;

  createdAt: string | null;
  updatedAt: string | null;
  isOnline?: boolean;
}
export interface ACPanelState {
  powerState: number;
  model: number;
  temperature: number;
  windspeed: number;
}

export interface DeviceDataAPIInfo {
  Did: string;
  Pid: string;
  Data: any;
}

export interface DeviceDataInfo {
  id: string;
  did: string;
  pid: string;
  data: any;
  isOnline?: boolean;
}

export interface deviceTileObject {
  deviceTitle: string;
  deviceIcon: deviceIconObject;
  deviceComponent: any;
  pid: string[];
}
export interface deviceIconObject {
  iconSet: string;
  icon: string;
  hasOtherSet?: boolean;
  iconSet2: string;
  icon2: string;
}

export enum ConnectionState {
  Advertising = 'Advertising',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Stopped = 'Stopped',
  Starting = 'Starting',
}

export enum LockSpec {
  Lock = 'Lock',
  LockBad = 'LockBad',
  Open = 'Open',
  OpenBad = 'OpenBad',
  Unknown = 'Unknown',
  Abnormal = 'Abnormal',
  Without = 'Without',
}
export interface LockData {
  deviceCode: string;
  bluetoothState: ConnectionState;
  lockStatus: LockSpec;
}

export interface ImportDeviceData {
  meterId: string;
  eSim: string;
  initialVal?: number;
  uploadFrequency?: string;
  uploadDays?: string;

  customerId?: number;
}

export interface UpdateDeviceDataAdmin {
  meterId: string;
  eSim: string;
  initialVal?: number;
  uploadFrequency?: string;
  uploadDays?: string;
}

export interface UpdateDeviceDataCustomer {
  meterId: string;
  initialVal?: number;

  clientMeterVendor?: string;
  clientMeterId?: string;
  clientMeterType?: string;
  clientMeterSize?: string;

  clientDeviceId?: string;
  city?: string;
  address?: string;
  settingCondition?: string;
  memo?: string;
}

// initialState:
export const initImportDeviceData = {
  meterId: '',
  eSim: '',
  initialVal: undefined,
  uploadFrequency: undefined,
  uploadDays: undefined,
  customerId: undefined,
};

export const initUpdateDeviceDataAdmin = {
  meterId: '',
  eSim: '',
  initialVal: undefined,
  uploadFrequency: undefined,
  uploadDays: undefined,
};

export const initUpdateDeviceDataCustomer = {
  meterId: '',
  initialVal: undefined,

  clientMeterVendor: '',
  clientMeterId: '',
  clientMeterType: '',
  clientMeterSize: '',

  clientDeviceId: '',
  city: '',
  address: '',
  settingCondition: '',
  memo: '',
};

// Strings:
export const ImportDeviceKey = {
  meterId: 'デバイス管理番号',
  eSim: 'eSIM番号',
  initialVal: '初針値',
  uploadFrequency: '検針期間単位',
  uploadDays: '希望検針日',

  customerId: 'お客様システム番号',
};

export const UpdateDeviceKeyAdmin = {
  meterId: 'デバイス管理番号',
  eSim: 'eSIM番号',
  initialVal: '初針値',
  uploadFrequency: '検針期間単位',
  uploadDays: '希望検針日',
};

export const UpdateDeviceKeyCustomer = {
  meterId: 'デバイス管理番号',
  initialVal: '初針値',

  clientMeterVendor: 'メーカー名',
  clientMeterId: 'メーター番号',
  clientMeterType: 'メーター型番',
  clientMeterSize: 'メーターサイズ',

  clientDeviceId: 'お客様内使用デバイス管理番号',
  city: '都道府県・市区町村',
  address: '場所',
  settingCondition: '設置状況',
  memo: 'メモ',
};

export const permissionArrayObject = {
  deviceDisplay: 'DEVICE_DISPLAY',
  deviceControl: 'DEVICE_CONTROL',
  groupEdit: 'GROUP_EDIT',
  groupDelete: 'GROUP_DELETE',
  homeQR: 'HOME_QR',
  homeInit: 'HOME_INIT',
  homeEdit: 'HOME_EDIT',
  homeMove: 'HOME_MOVE',
  homeDelete: 'HOME_DELETE',
  notificationAll: 'NOTIFICATION_ALL',
  admin: 'admin',
  doctor: 'DOCTOR',
  drug: 'DRUG',
  hydro: 'figma-admin',
  mamoleo: 'mamoleo-admin',
  management: 'MANAGEMENT',
  sekiadmin: 'seki816T-admin',
  accountManager: 'account-manager',
};

export type {
  IAutoAction,
  ITempPermission,
  ITempPermissionEmail,
} from './tempPermission';

export const tempPermissionAutoStart = {
  autoAC: 'autoAC',
  autoLock: 'autoLock',
  akuvoxPin: 'akuvoxPin',
};

export enum ControlActionType {
  panel = 'panel',
  scene = 'scene',
  notify = 'notify',
  device = 'device',
}

export interface DeviceControlAction {
  type: ControlActionType.device; // NOTE: historical device control actions might have arbitrary string here.
  deviceId: string;
  operation: string;
  extraInfo?: any;
  lockKey?: string; // lock identifier to acquire before performing action
  lockDuration?: number; // duration in seconds to hold the lock
}
export interface DeviceData {
  parentId?: string;
  name: string;
  familyId: string;
  roomId?: string | null; // May reference to deleted room, use null to reset roomId.
  pid: string;
  appearance?: string;
  did: string;
  vendor: string;
  hidden?: boolean; // Don't display in top screen.
  order?: number;
  accessoryOrders?: { [index: string]: number | undefined };
  orderInRoom?: number;
  accessoryOrdersInRoom?: { [index: string]: number | undefined };
  extraInfo?: any;
  setting?: any; // User setting of the device, can be changed by QRLink user.
}

export type Device = {
  id: string;
  linked?: boolean;
  createdAt: string;
  updatedAt: string;
} & DeviceData;

export type AppliancePanelType =
  | 'tv'
  | 'ac'
  | 'light'
  | 'recorder'
  | 'fan'
  | 'humidifier'
  | 'cleaner'
  | 'theater'
  | 'kitchen_hood'
  | 'custom';

export interface BaseControlAction {
  type: ControlActionType | string;
}

export interface PanelControlAction extends BaseControlAction {
  deviceId: string;
  panelId: string;
  panelType: AppliancePanelType;
  irCode: string;
  irCodeKey: string;
  extraInfo?: any;
}

export interface SceneControlAction extends BaseControlAction {
  sceneId: string;
}

export interface DeviceControlAction extends BaseControlAction {
  deviceId: string;
  operation: string;
  extraInfo?: any;
}

export interface NotifyAction extends BaseControlAction {
  deviceId?: string;
  operation: ControlActionType.notify;
  extraInfo: {
    notificationText: string;
  };
}

export type ControlAction =
  | PanelControlAction
  | SceneControlAction
  | DeviceControlAction
  | NotifyAction;

export interface CountdownData {
  familyId: string;
  deviceId: string;
  panelId?: string;
  offAction: ControlAction;
  seconds: number;
}

export type Countdown = {
  id: string;
  willTriggerAt: string;
  createdAt: string;
  updatedAt: string;
} & CountdownData;

export interface ChatUser {
  uid: string;
  name?: string | null;
  image?: string | null;
  appKey: string;
  role: string;
  token: string;
  lastActiveAt?: string | null;
  createdAt: string;
  updatedAt: string;
  isDeleted?: boolean;
}

export interface ChatChannel {
  type: string;
  appKey: string;
  cid: string;
  name?: string | null;
  image?: string | null;
  createdBy: string;
  memberCount: number;
  lastMessageAt?: string | null;
  lastUpdatedAt: string;
  createdAt: string;
  updatedAt: string;
  createdByUser: ChatUser;
  members: ChatUser[];
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: string;
  type: string;
  appKey: string;
  cid: string;
  uid: string;
  text: string;
  html?: string | null;
  mentionedUids: string[];
  extraInfo: any;
  attachments: ChatMessageAttachment[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  status?: string | null;
  user: ChatUser;
  mentionedUsers: ChatUser[];
  showDate?: boolean;
}

export interface ChatMessageAttachment {
  type?: string | null;
  authorName?: string | null;
  title?: string | null;
  titleLink?: string | null;
  text?: string | null;
  imageURL?: string | null;
  thumbURL?: string | null;
  assetURL?: string | null;
  ogScrapeURL?: string | null;
  extraInfo?: any;
}

export type Account = {
  id: string;
  app: string;
  company?: string;
  createdAt: string;
  email: string;
  lastLoginAt: string;
  name: string;
  parentAccountId?: string;
  permissions?: string;
  updatedAt: string;
};

export type ChatChannelUnreadCount = {
  appKey: string;
  cid: string;
  lastMessageAt: string;
  markReadAt: string | null;
  unreadCount: number;
};

export type UnreadCountApiRes = {
  totalUnreadCount: number;
  unreadChannels: ChatChannelUnreadCount[];
};

export type MarkChannelReadApiRes = ChatChannelUnreadCount & {
  unreadChannelsResult: {
    unreadChannels: ChatChannelUnreadCount[];
  };
};

export enum ACKey {
  SWITCH,
  MODE,
  TEMP_ADD,
  TEMP_SUB,
  WIND_SPPED,
}

export enum ACStatus {
  OFF,
  ON,
}

export enum ACMode {
  AUTO,
  COOL,
  HUMIDITY,
  WIND,
  HEAT,
}

export enum ACWindspeed {
  AUTO,
  LOW,
  MID,
  HIGH,
}
export interface BackendACIRCodeMeta {
  minTemp: number;
  maxTemp: number;
  powerState: ACStatus[];
  windspeed: ACWindspeed[];
  model: ACMode[];
}

export interface BackendIRCodeSpec {
  id: string;
  brandId: number;
  type: string;
  functionList?: {
    function: string;
    code: number[];
    hexCode: string;
  }[];
  meta?: BackendACIRCodeMeta;
  weight?: number;
  createdAt: string;
  updatedAt: string;
}
// don't observe every field cuz code is large
export class IRCode {
  key: string;
  code: string;
  @observable
  name?: string;
  @observable
  desc?: string;
  constructor({
    key,
    code,
    name,
    desc,
  }: {
    key: string;
    code: string;
    name?: string;
    desc?: string;
  }) {
    this.key = key;
    this.code = code;
    this.name = name;
    this.desc = desc;
  }
}

export interface ACStateSpec {
  status: ACStatus[];
  mode: ACMode[];
  windspeed: ACWindspeed[];
  minTemperature: number;
  maxTemperature: number;
}

export class IRCodeSpec {
  id: string;
  type: AppliancePanelType;
  brandId: number;
  codes: IRCode[];
  acStateSpec?: ACStateSpec;
  weight?: number;
  timestamp: number;
  constructor({
    id,
    type,
    brandId,
    codes,
    acStateSpec,
    timestamp,
  }: {
    id: string;
    type: AppliancePanelType;
    brandId: number;
    codes: IRCode[];
    acStateSpec?: ACStateSpec;
    timestamp: number;
  }) {
    this.id = id;
    this.type = type;
    this.brandId = brandId;
    this.codes = codes;
    this.acStateSpec = acStateSpec;
    this.timestamp = timestamp;
  }
}
